/**
 * Google Merchant Listing (Sælgerfortegnelser) Offer helpers.
 *
 * Search Console flags Product offers that already declare shippingDetails
 * without shippingDestination, and Offers without validFrom.
 * @see https://developers.google.com/search/docs/appearance/structured-data/merchant-listing
 */
import { LANGUAGE_TO_DOMAIN, type Language } from '@/utils/languageSwitching';
import { normalizeSiteLanguage } from '@/utils/routing';

export type MarketCountry = 'SK' | 'CZ' | 'DK' | 'US' | 'DE';

const LANGUAGE_TO_COUNTRY: Record<Language, MarketCountry> = {
  sk: 'SK',
  cs: 'CZ',
  da: 'DK',
  en: 'US',
  de: 'DE',
};

// English pages are written for the UK market (GBP, British rules).
export type OfferCountry = MarketCountry | 'GB';

export function marketCountryFromLanguage(language?: string): OfferCountry {
  const lang = normalizeSiteLanguage(language);
  return lang === 'en' ? 'GB' : LANGUAGE_TO_COUNTRY[lang];
}

export function marketDomainFromLanguage(language?: string): string {
  return LANGUAGE_TO_DOMAIN[normalizeSiteLanguage(language)];
}

export function marketCountryFromHostname(hostname?: string): OfferCountry | null {
  if (!hostname) return null;
  const host = hostname.toLowerCase();
  if (host.includes('.dk')) return 'DK';
  if (host.includes('.cz')) return 'CZ';
  if (host.includes('.com')) return 'GB';
  if (host.includes('.sk')) return 'SK';
  return null;
}

export function isoDateOnly(date: Date = new Date()): string {
  return date.toISOString().split('T')[0];
}

export function offerValidityWindow(now: Date = new Date()): {
  validFrom: string;
  priceValidUntil: string;
} {
  const until = new Date(now.getTime());
  until.setUTCFullYear(until.getUTCFullYear() + 1);
  return {
    validFrom: isoDateOnly(now),
    priceValidUntil: isoDateOnly(until),
  };
}

export function definedRegion(country: OfferCountry) {
  return {
    '@type': 'DefinedRegion',
    addressCountry: country,
  };
}

function sameDayWindow() {
  return {
    '@type': 'QuantitativeValue',
    minValue: 0,
    maxValue: 0,
    unitCode: 'DAY',
  };
}

/** Instant digital delivery — Bidmio is SaaS, not a physical shipment. */
export function digitalShippingDetails(currency: string, country: OfferCountry) {
  return {
    '@type': 'OfferShippingDetails',
    shippingRate: {
      '@type': 'MonetaryAmount',
      value: '0',
      currency,
    },
    shippingDestination: definedRegion(country),
    deliveryTime: {
      '@type': 'ShippingDeliveryTime',
      handlingTime: sameDayWindow(),
      transitTime: sameDayWindow(),
    },
  };
}

export function merchantReturnPolicy(country: OfferCountry) {
  return {
    '@type': 'MerchantReturnPolicy',
    applicableCountry: country,
    returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
    merchantReturnDays: 14,
    returnMethod: 'https://schema.org/ReturnByMail',
    returnFees: 'https://schema.org/FreeReturn',
  };
}
